// ResourceEditDialog.tsx
import React, { useEffect, useState } from 'react';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { useTranslation } from 'react-i18next';

type ResourceDto = {
  id: number;
  title: string;
  code: string;
  label: string;
  url: string;
  attachedResourceIds?: string | null;
};

interface Props {
  resourceId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const ResourceEditDialog: React.FC<Props> = ({ resourceId, isOpen, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [attachedResourceIds, setAttachedResourceIds] = useState('');
  const [orig, setOrig] = useState<ResourceDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isOpen || !resourceId) return;

    setLoading(true);

    axiosInstance
      .get<ResourceDto>(ApiConstants.utility.getResource, {
        params: { resourceId },
      })
      .then((res) => {
        setOrig(res.data);
        setTitle(res.data.title ?? '');
        setCode(res.data.code ?? '');
        setLabel(res.data.label ?? '');
        setUrl(res.data.url ?? '');
        setAttachedResourceIds(res.data.attachedResourceIds ?? '');
      })
      .finally(() => setLoading(false));
  }, [isOpen, resourceId]);

  const save = async () => {
    if (!resourceId || !orig) return;

    const originalAttached = (orig.attachedResourceIds ?? '').trim();
    const nextAttached = attachedResourceIds.trim();
    const hasChanges =
      title !== orig.title ||
      code !== orig.code ||
      label !== orig.label ||
      url !== orig.url ||
      nextAttached !== originalAttached;

    if (!hasChanges) return;

    const payload = {
      id: resourceId,
      title,
      code,
      label,
      url,
      attachedResourceIds: nextAttached || null,
    };

    setLoading(true);
    try {
      await axiosInstance.post(ApiConstants.utility.saveResource, payload);
      onSaved();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <EditModal
      title={i18n.t('roles.editResource')}
      modalType={eFormType.Edit}
      width="600px"
      setIsModalOpen={onClose}
      closeModal={onClose}
      onSubmitData={save}
    >
      <div className="ui-form">
        <div className="ui-field">
          <label>{i18n.t('productCategory.title')}</label>
          <input
            className="ui-control ui-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
            placeholder="Tables"
          />
        </div>

        <div className="ui-field">
          <label>{i18n.t('generic.label')}</label>
          <input
            className="ui-control ui-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={loading}
            placeholder="tables"
          />
        </div>

        <div className="ui-field">
          <label>{i18n.t('generic.code')}</label>
          <input className="ui-input" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>

        <div className="ui-field">
          <label>URL</label>
          <input className="ui-input" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>

        <div className="ui-field">
          <label>Attached Resource Ids</label>
          <input
            className="ui-input"
            value={attachedResourceIds}
            onChange={(e) => setAttachedResourceIds(e.target.value)}
            placeholder="ex. 12,15,18"
          />
        </div>
      </div>
    </EditModal>
  );
};

export default ResourceEditDialog;
